import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack, // (Note: Stack was imported but unused, kept it just in case)
  useMediaQuery, // (Note: unused in snippet, kept it)
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  Scale,
} from "chart.js";
import "./OverviewChart.scss";
import { dayBoundaryPlugin } from "../../Components/ActivityChart/ActivityChart";
import { TrendData } from "../../Models/Types";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface OverviewChartProps {
  trendData: TrendData | null;
  loading: boolean;
}

const OverviewChart: React.FC<OverviewChartProps> = ({
  trendData,
  loading,
}) => {
  // --- New State for Visibility ---
  const [isChartVisible, setIsChartVisible] = useState(false);

  // State: How many hours back from the most recent time are we?
  const [offsetHours, setOffsetHours] = useState(0);

  // --- Constants ---
  const WINDOW_SIZE_HOURS = 6;
  const WINDOW_SIZE_MS = WINDOW_SIZE_HOURS * 60 * 60 * 1000;

  // --- Chart Data Calculation ---
  const chartConfig = useMemo(() => {
    if (!trendData) return null;

    // 1. Get all timestamps sorted
    const allLabels = Object.keys(trendData.monitor_count).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );

    if (allLabels.length === 0) return null;

    // 2. Determine Global Boundaries
    const globalEndTime = new Date(allLabels[allLabels.length - 1]).getTime();
    const globalStartTime = new Date(allLabels[0]).getTime();

    // 3. Determine Current Window Boundaries based on Offset
    const currentWindowEnd = globalEndTime - offsetHours * 60 * 60 * 1000;
    const currentWindowStart = currentWindowEnd - WINDOW_SIZE_MS;

    // 4. Filter Labels for this window
    let visibleLabels = allLabels.filter((label) => {
      const t = new Date(label).getTime();
      return t > currentWindowStart && t <= currentWindowEnd;
    });

    // 5. Extract Data for visible labels
    var dataOk = visibleLabels.map((l) => trendData.monitor_ok[l] || 0);
    var dataKo = visibleLabels.map((l) => trendData.monitor_ko[l] || 0);
    // var dataBo = visibleLabels.map((l) => trendData.monitor_bo[l] || 0);

    // 7. Format X-Axis Labels
    const formattedLabels = visibleLabels.map((l) =>
      new Date(l).toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    );

    // Calculate if we can navigate further
    const canGoBack = currentWindowStart > globalStartTime;
    const canGoForward = offsetHours > 0;

    return {
      data: {
        labels: formattedLabels,
        datasets: [
          {
            label: "KO",
            data: dataKo,
            borderColor: "#ff5252",
            backgroundColor: "rgba(255, 82, 82, 0.4)",
            tension: 0.1,
            pointRadius: 0,
            // KO remains visible by default
          },
          {
            label: "OK",
            data: dataOk,
            borderColor: "#66bb6a",
            backgroundColor: "rgba(102, 187, 106, 0.2)",
            tension: 0.1,
            pointRadius: 0,
            hidden: true, // <--- CHANGED: Hidden by default, but visible in legend
          },
        ],
      },
      canGoBack,
      canGoForward,
    };
  }, [trendData, offsetHours]);

  // --- Handlers ---
  const handlePrev = () => {
    setOffsetHours((prev) => prev + WINDOW_SIZE_HOURS);
  };

  const handleNext = () => {
    setOffsetHours((prev) => Math.max(0, prev - WINDOW_SIZE_HOURS));
  };

  // --- Chart Options ---
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: { boxWidth: 40, padding: 25, font: { size: 10 } },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        ticks: {
          // Enable autoSkip so Chart.js can manage the density
          autoSkip: true,
          // Limit ticks to ~13 (approx. one every 30 mins for a 6h window)
          maxTicksLimit: 13,
          maxRotation: 0,
          minRotation: 0,
          font: { size: 12 },
          // Removed the strict string-matching callback completely
        },
        grid: { color: "#f0f0f0" },
      },
      y: {
        display: true,
        grid: { color: "#f0f0f0" },
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  };

  // --- RENDER LOGIC ---

  // If chart is visible, show the full component
  return (
    <div className="section-container bg-gray">
      {/* --- Sostituzione dello Switch con l'Accordion --- */}
      <Accordion
        elevation={0}
        sx={{
          background: "transparent",
          "&:before": { display: "none" },
        }}
        expanded={isChartVisible}
        onChange={(e, expanded) => setIsChartVisible(expanded)}
      >
        <AccordionSummary
          expandIcon={
            <ChevronLeftIcon
              sx={{
                transform: isChartVisible ? "rotate(90deg)" : "rotate(-90deg)",
                transition: "transform 0.2s", // Optional: adds a smooth spin
              }}
            />
          }
          sx={{
            minHeight: 40,
            ".MuiAccordionSummary-content": { my: 0 },
            padding: 0,
            // Disable MUI's default 180deg rotation on the wrapper
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "none",
            },
          }}
        >
          <Typography className="section-title" sx={{ m: 0 }}>
            Grafico trend
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Contenuto del grafico */}
          <Box>
            {/* Navigation Buttons */}
            {chartConfig && (
              <Box className="chart-nav-buttons" sx={{ mt: 1, mb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                  onClick={handlePrev}
                  disabled={!chartConfig?.canGoBack || loading}
                >
                  -6 Ore
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  onClick={handleNext}
                  disabled={!chartConfig?.canGoForward || loading}
                >
                  +6 Ore
                </Button>
              </Box>
            )}

            <Box sx={{ height: 250, width: "100%", pb: 2 }}>
              {chartConfig ? (
                <Line
                  data={chartConfig.data}
                  options={chartOptions}
                  plugins={[dayBoundaryPlugin]}
                />
              ) : (
                <Typography
                  align="center"
                  sx={{ mt: 10, color: "text.secondary" }}
                >
                  {loading ? <CircularProgress /> : "Nessun dato"}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default OverviewChart;
